/**
 * Record type for a chart-document QuestionnaireResponse (treatment plan, safety plan, consent, …).
 *
 * Chart documents used to fall through `sessionNoteTypeFromQuestionnaire`, which defaults to
 * "Progress Note" — so every unrecognised document was filed as session documentation. Resolution
 * order here is: explicit title rule → Questionnaire template category → neutral fallback.
 */

import type { Questionnaire } from '@medplum/fhirtypes';
import { getTemplateCategory } from '../../pages/settings/template-library/utils/templateTags';
import type { ClinicalNote } from '../../pages/clients/data/types';

type ChartDocumentRecordType = ClinicalNote['noteType'];

/**
 * Ordered — the first match wins, so narrower phrases come first
 * ("Minor Custody Agreement & Consent Screening" is a custody agreement, not a consent form).
 */
const TITLE_RULES: readonly (readonly [RegExp, ChartDocumentRecordType])[] = [
  [/release of information|\broi\b/, 'ROIs'],
  [/discharge/, 'Discharge Summary'],
  [/transfer/, 'Transfer Request'],
  [/safety plan/, 'Safety Plan'],
  [/risk assessment/, 'Risk Assessment'],
  [/treatment plan|treatment review|continuing recovery plan/, 'Treatment Plan'],
  // Narrower legal/form phrases before the broad consent rule.
  [/custody/, 'Custody Agreements'],
  [/good faith estimate/, 'Good Faith Estimates'],
  [/court|legal proceeding/, 'Court Documents'],
  [/legal hold/, 'Legal Holds'],
  [/incident/, 'Incident Reports'],
  [/eligibility/, 'Eligibility Notes'],
  [/waitlist/, 'Waitlist Entry'],
  [/collateral/, 'Collateral Contact Note'],
  [/contact log/, 'Contact Logs'],
  [/inquiry/, 'Inquiry Note'],
  // PHI access responses are legal/privacy paperwork, not treatment plans.
  [/inspect.*phi|receive a copy of phi/, 'Consent Forms'],
  // Consent before lab so "Lab Request Informed Consent" stays a form, not a lab order.
  [/consent|informed consent|privacy practices|client rights|admissions agreement/, 'Consent Forms'],
  [/lab request|lab order/, 'Lab Orders'],
  [/intake form|intake assessment|biopsychosocial|history form/, 'Intake Forms'],
  [/chart note|supervision note/, 'Chart Note'],
];

/** Fallback when no title rule matches: keep the document in its Template Library category. */
const CATEGORY_FALLBACK: Readonly<Record<string, ChartDocumentRecordType>> = {
  'treatment-planning': 'Treatment Plan',
  forms: 'Consent Forms',
  administrative: 'Chart Note',
  measures: 'Assessments',
};

export function resolveChartDocumentRecordType(
  questionnaire: Questionnaire | undefined,
  options?: { isContactRoi?: boolean },
): ChartDocumentRecordType {
  if (options?.isContactRoi) return 'ROIs';

  const title = (questionnaire?.title ?? questionnaire?.name ?? '').toLowerCase();
  for (const [pattern, recordType] of TITLE_RULES) {
    if (pattern.test(title)) return recordType;
  }

  const category = questionnaire ? getTemplateCategory(questionnaire) : undefined;
  return (category && CATEGORY_FALLBACK[category]) || 'Chart Note';
}
